import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useMemo } from "react";
import type { GeoDirectionalLine, GeoScatterBoundary, GeoScatterPoint } from "@/shared/charts";
import type { SiteTotalPopulationChart } from "@/features/deployment-dashboard/live-view/hooks/use-site-total-population-chart";
import { DataCollectionNotice } from "@/features/deployment-dashboard/widgets/data-collection-notice";
import { FacilitySchematic } from "@/features/deployment-dashboard/widgets/panels/facility-schematic";
import type {
  LiveTrackPoint,
  LiveTrailSegment,
  ObservationAreaBoundary,
  SensorMarkerPoint,
  AreaLocation,
} from "@/features/deployment-dashboard/types";
import { LiveViewFilterBar } from "@/features/deployment-dashboard/live-view/live-view-filter-bar";
import {
  LiveViewSidePanel,
  type LiveViewSidebarTab,
} from "@/features/deployment-dashboard/live-view/live-view-side-panel";
import {
  LiveViewTabs,
  type LiveViewMode,
} from "@/features/deployment-dashboard/live-view/components/live-view-tabs";
import { PopulationChartView } from "@/features/deployment-dashboard/live-view/components/population-chart-view";
import type {
  Aoi,
  AoiDetailsInput,
  AoiEditorMode,
  AoiWorldPoint,
} from "@/features/deployment-dashboard/live-view/model/aoi";
import type { LiveViewTimeRange } from "@/features/deployment-dashboard/live-view/model/time-range";
import type { LiveViewStats } from "@/features/deployment-dashboard/live-view/utils/derive-live-view-stats";
import type { LiveViewQueue } from "@/features/deployment-dashboard/live-view/model/queue";
import { usePageTitle, useTopBarFilters } from "@/shared/ui";
import type { WorldToPixelTransformAsset } from "@/features/deployment-dashboard/widgets/panels/facility-schematic/services/world-to-pixel-transform";
import type { AreaCurrentPopulationState } from "@/features/deployment-dashboard/shared/hooks/use-area-current-population";
import type { LiveStreamStatus } from "@/features/deployment-dashboard/live-view/hooks/use-live-tracks-stream";
import type { SecurityLaneStatuses } from "@/features/deployment-dashboard/live-view/utils/derive-security-lane-states";

interface LiveViewViewProps {
  deploymentId: string;
  /** Primary deployment area whose map/floor plan is displayed, if any is available yet. */
  areaId?: string;
  liveTracks: LiveTrackPoint[];
  heatmapTracks: LiveTrackPoint[];
  heatmapTransform?: Pick<WorldToPixelTransformAsset, "matrix">;
  heatmapMapSize?: { width: number; height: number };
  areas: AreaLocation[];
  /** Selected area id to subscribe to. */
  selectedAreaId: string | null;
  onSelectedAreaIdChange: (areaId: string | null) => void;
  showHeatmap: boolean;
  onShowHeatmapChange: (showHeatmap: boolean) => void;
  sensorPoints: SensorMarkerPoint[];
  showSensors: boolean;
  onShowSensorsChange: (showSensors: boolean) => void;
  showDetections: boolean;
  onShowDetectionsChange: (showDetections: boolean) => void;
  trails: LiveTrailSegment[];
  showTracks: boolean;
  onShowTracksChange: (showTracks: boolean) => void;
  /** Observation area boundaries to highlight, already mapped into the schematic's pixel coordinate space. */
  observationAreas?: ObservationAreaBoundary[];
  aois: Aoi[];
  selectedAoiId: string | null;
  onToggleAoi: (aoiId: string) => void;
  canModifyAois: boolean;
  aoiEditorMode: AoiEditorMode;
  editingAoiId: string | null;
  aoiDraftPoints: AoiWorldPoint[];
  aoiBoundaries: GeoScatterBoundary[];
  aoiDraftPointMarkers: GeoScatterPoint[];
  isSavingAoi: boolean;
  aoiError: string | null;
  onStartCreateAoi: () => void;
  onStartEditAoi: (aoi: Aoi) => void;
  onCancelAoiEdit: () => void;
  onUndoAoiPoint: () => void;
  onClearAoiPoints: () => void;
  onDeleteAoiPoint: (pointIndex: number) => void;
  onMoveAoiPoint: (pointId: string, point: AoiWorldPoint) => void;
  onMapAoiPoint: (point: AoiWorldPoint) => void;
  onInsertAoiPoint: (insertionPointId: string) => void;
  aoiInsertionPointMarkers: GeoScatterPoint[];
  onHoverAoiPoint: (pointId: string | null) => void;
  hoveredAoiPointIndex: number | null;
  onSelectAoiAtPoint: (point: AoiWorldPoint) => void;
  onDeleteAoi: (aoiId: string) => void;
  canDeleteAoi: boolean;
  onSaveAoi: (input: AoiDetailsInput) => void;
  aoiPopulations: Record<string, number>;
  aoiDensities: Record<string, number | null>;
  securityLaneStatuses: SecurityLaneStatuses;
  populationLabel: string;
  populationCount: number;
  liveViewStats: LiveViewStats;
  areaCurrentPopulation: AreaCurrentPopulationState;
  liveTracksStatus: LiveStreamStatus;
  liveTracksHasFrame: boolean;
  queues: LiveViewQueue[];
  selectedQueueId: string | null;
  onToggleQueue: (queueId: string) => void;
  queueBoundaries: GeoScatterBoundary[];
  queueDirectionalLines: GeoDirectionalLine[];
  activeSidebarTab: LiveViewSidebarTab;
  onActiveSidebarTabChange: (tab: LiveViewSidebarTab) => void;
  queuesLoading?: boolean;
  queuesError?: Error | null;
  summaryTitle: string;
  isSummaryExpanded: boolean;
  onSummaryExpandedChange: (expanded: boolean) => void;
  viewMode: LiveViewMode;
  onViewModeChange: (viewMode: LiveViewMode) => void;
  timeRange: LiveViewTimeRange;
  onTimeRangeChange: (timeRange: LiveViewTimeRange) => void;
  populationChart: SiteTotalPopulationChart | undefined;
  populationChartIsLoading: boolean;
  populationChartError: Error | null;
  populationChartHasGrant: boolean;
  /** Bump this to re-focus the map on the currently selected area(s). */
  focusAreasRequestId: number;
  /** Optional callback to reset/refocus the view on the currently selected area(s). */
  onResetFocus?: () => void;
  /** Whether the map is currently focused on the selected area(s), or has been manually panned/zoomed away from that focus. */
  isAreasFocused: boolean;
  onAreasFocusedChange: (focused: boolean) => void;
}

export function LiveViewView({
  deploymentId,
  areaId,
  liveTracks,
  heatmapTracks,
  heatmapTransform,
  heatmapMapSize,
  areas,
  selectedAreaId,
  onSelectedAreaIdChange,
  showHeatmap,
  onShowHeatmapChange,
  sensorPoints,
  showSensors,
  onShowSensorsChange,
  showDetections,
  onShowDetectionsChange,
  trails,
  showTracks,
  onShowTracksChange,
  observationAreas = [],
  aois,
  selectedAoiId,
  onToggleAoi,
  canModifyAois,
  aoiEditorMode,
  editingAoiId,
  aoiDraftPoints,
  aoiBoundaries,
  aoiDraftPointMarkers,
  isSavingAoi,
  aoiError,
  onStartCreateAoi,
  onStartEditAoi,
  onCancelAoiEdit,
  onUndoAoiPoint,
  onClearAoiPoints,
  onDeleteAoiPoint,
  onMoveAoiPoint,
  onMapAoiPoint,
  onInsertAoiPoint,
  aoiInsertionPointMarkers,
  onHoverAoiPoint,
  hoveredAoiPointIndex,
  onSelectAoiAtPoint,
  onDeleteAoi,
  canDeleteAoi,
  onSaveAoi,
  aoiPopulations,
  aoiDensities,
  securityLaneStatuses,
  populationLabel,
  populationCount,
  liveViewStats,
  areaCurrentPopulation,
  liveTracksStatus,
  liveTracksHasFrame,
  queues,
  selectedQueueId,
  onToggleQueue,
  queueBoundaries,
  queueDirectionalLines,
  activeSidebarTab,
  onActiveSidebarTabChange,
  queuesLoading = false,
  queuesError = null,
  summaryTitle,
  isSummaryExpanded,
  onSummaryExpandedChange,
  viewMode,
  onViewModeChange,
  timeRange,
  onTimeRangeChange,
  populationChart,
  populationChartIsLoading,
  populationChartError,
  populationChartHasGrant,
  focusAreasRequestId,
  onResetFocus,
  isAreasFocused: _isAreasFocused,
  onAreasFocusedChange,
}: LiveViewViewProps) {
  const pageTitle = useMemo(
    () => <LiveViewTabs value={viewMode} onChange={onViewModeChange} />,
    [viewMode, onViewModeChange]
  );
  usePageTitle(pageTitle);
  const filters = useMemo(
    () => (
      <LiveViewFilterBar
        areas={areas}
        selectedAreaId={selectedAreaId}
        onSelectedAreaIdChange={onSelectedAreaIdChange}
        showHeatmap={showHeatmap}
        onShowHeatmapChange={onShowHeatmapChange}
        showSensors={showSensors}
        onShowSensorsChange={onShowSensorsChange}
        showDetections={showDetections}
        onShowDetectionsChange={onShowDetectionsChange}
        showTracks={showTracks}
        onShowTracksChange={onShowTracksChange}
        viewMode={viewMode}
        timeRange={timeRange}
        onTimeRangeChange={onTimeRangeChange}
      />
    ),
    [
      areas,
      selectedAreaId,
      onSelectedAreaIdChange,
      showHeatmap,
      onShowHeatmapChange,
      showSensors,
      onShowSensorsChange,
      showDetections,
      onShowDetectionsChange,
      showTracks,
      onShowTracksChange,
      viewMode,
      timeRange,
      onTimeRangeChange,
    ]
  );
  useTopBarFilters(filters);
  const populationStatusText =
    aoiEditorMode === "create"
      ? "Creating new area of interest…"
      : aoiEditorMode === "edit"
        ? "Editing " + populationLabel + "…"
        : populationLabel + ": " + populationCount + " people";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
        height: "100%",
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", gap: 2.5 }}>
        <LiveViewSidePanel
          title={summaryTitle}
          stats={liveViewStats}
          areaCurrentPopulation={areaCurrentPopulation}
          liveTracksStatus={liveTracksStatus}
          liveTracksHasFrame={liveTracksHasFrame}
          queues={queues}
          selectedQueueId={selectedQueueId}
          onToggleQueue={onToggleQueue}
          queuesLoading={queuesLoading}
          queuesError={queuesError}
          areas={areas}
          displayedAreaId={areaId}
          activeTab={activeSidebarTab}
          onActiveTabChange={onActiveSidebarTabChange}
          expanded={isSummaryExpanded}
          onExpandedChange={onSummaryExpandedChange}
          aois={aois}
          selectedAoiId={selectedAoiId}
          onToggleAoi={onToggleAoi}
          canModifyAois={canModifyAois}
          aoiInteractionsEnabled={viewMode === "map"}
          editorMode={aoiEditorMode}
          editingAoiId={editingAoiId}
          draftPoints={aoiDraftPoints}
          isSavingAoi={isSavingAoi}
          aoiError={aoiError}
          onStartCreateAoi={onStartCreateAoi}
          onStartEditAoi={onStartEditAoi}
          onCancelAoiEdit={onCancelAoiEdit}
          onUndoAoiPoint={onUndoAoiPoint}
          onClearAoiPoints={onClearAoiPoints}
          onDeleteAoiPoint={onDeleteAoiPoint}
          onHoverAoiPoint={aoiEditorMode === null ? undefined : onHoverAoiPoint}
          hoveredAoiPointIndex={hoveredAoiPointIndex}
          onDeleteAoi={onDeleteAoi}
          canDeleteAoi={canDeleteAoi}
          onSaveAoi={onSaveAoi}
          aoiPopulations={aoiPopulations}
          aoiDensities={aoiDensities}
          securityLaneStatuses={securityLaneStatuses}
        />
        {viewMode === "map" ? (
          <Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
            <FacilitySchematic
              deploymentId={deploymentId}
              areaId={areaId}
              liveTracks={showDetections ? liveTracks : []}
              trails={showTracks ? trails : []}
              heatmapTracks={heatmapTracks}
              heatmapTransform={heatmapTransform}
              heatmapMapSize={heatmapMapSize}
              showHeatmap={showHeatmap}
              sensorPoints={sensorPoints}
              showSensors={showSensors}
              observationAreas={observationAreas}
              observationAreaTooltipsEnabled={aoiEditorMode === null}
              aoiBoundaries={activeSidebarTab === "areasOfInterest" ? aoiBoundaries : []}
              queueBoundaries={activeSidebarTab === "queues" ? queueBoundaries : []}
              directionalLines={activeSidebarTab === "queues" ? queueDirectionalLines : []}
              aoiDraftPoints={activeSidebarTab === "areasOfInterest" ? aoiDraftPointMarkers : []}
              aoiInsertionPoints={
                activeSidebarTab === "areasOfInterest" ? aoiInsertionPointMarkers : []
              }
              onInsertAoiDraftPoint={
                activeSidebarTab === "areasOfInterest" ? onInsertAoiPoint : undefined
              }
              onMapPointClick={
                activeSidebarTab === "areasOfInterest" && aoiEditorMode === "create"
                  ? onMapAoiPoint
                  : undefined
              }
              onMoveAoiDraftPoint={
                activeSidebarTab === "areasOfInterest" && aoiEditorMode !== null
                  ? onMoveAoiPoint
                  : undefined
              }
              canMoveAoiDraftShape={
                activeSidebarTab === "areasOfInterest" && aoiEditorMode === "edit"
              }
              onHoverAoiDraftPoint={
                activeSidebarTab === "areasOfInterest" && aoiEditorMode !== null
                  ? onHoverAoiPoint
                  : undefined
              }
              onAoiBoundaryDoubleClick={
                activeSidebarTab === "areasOfInterest" && aoiEditorMode === null
                  ? onSelectAoiAtPoint
                  : undefined
              }
              focusRequestId={focusAreasRequestId}
              onFocusedChange={onAreasFocusedChange}
              onResetFocus={onResetFocus}
            />
            <Paper
              aria-label={populationStatusText}
              elevation={8}
              sx={{
                position: "absolute",
                bottom: 1.25,
                left: "50%",
                zIndex: 2,
                transform: "translateX(-50%)",
                px: 2,
                py: 1,
                borderRadius: 1,
                color: "common.white",
                backgroundColor: "rgba(0, 0, 0, 0.75)",
                pointerEvents: "none",
                whiteSpace: "nowrap",
              }}
            >
              <Typography variant="h6" align="center" sx={{ fontWeight: 700 }}>
                {populationStatusText}
              </Typography>
            </Paper>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2.5 }}>
            <DataCollectionNotice />
            <PopulationChartView
              chart={populationChart}
              isLoading={populationChartIsLoading}
              error={populationChartError}
              hasGrant={populationChartHasGrant}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
